import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type SlotValue = {
  node: HTMLElement | null;
  setNode: (node: HTMLElement | null) => void;
};

const SlotContext = createContext<SlotValue | null>(null);

/**
 * Lets the component that owns a list's state put actions in a header owned by
 * its layout (APP-2510 D12). The actions stay in the owner's React tree, so
 * they read its live params rather than ones rebuilt from the URL.
 * `MSR3BaseLayout` provides it, so a page needs no wiring of its own.
 */
export const PageHeaderActionsSlot = ({ children }: PropsWithChildren) => {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ node, setNode }), [node]);

  return <SlotContext.Provider value={value}>{children}</SlotContext.Provider>;
};

/**
 * Where `PageHeaderActions` render. `PageHeader` puts one in its actions area,
 * after any `actions` the page passes it.
 */
export const PageHeaderActionsTarget = ({
  className,
}: {
  className?: string;
}) => {
  const slot = useContext(SlotContext);

  return (
    <div
      className={className}
      data-page-header-actions-target=""
      ref={slot?.setNode}
    />
  );
};

/**
 * Renders its children in the page header, after the header's own `actions`.
 * Nothing renders outside a `PageHeaderActionsSlot`.
 */
export const PageHeaderActions = ({ children }: PropsWithChildren) => {
  const node = useContext(SlotContext)?.node;

  return node ? createPortal(children, node) : null;
};
